import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { bringIntoView } from "../../ui/scroll";
import type { Menu, Product } from "../../api/types";
import { t } from "../../i18n";
import { eurosToCents, formatEuro } from "../../lib/money";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import {
  allergenView,
  ALLERGENS,
  suggestAllergens,
  suggestedCodes,
  whyText,
  withAllergens,
  useAllergenDictionary,
} from "./allergens";
import { YesNo } from "./CategoryDialog";
import {
  checkProduct,
  DEFAULT_SIZES,
  deleteProduct,
  duplicateDraft,
  followPrices,
  groupLabel,
  parseEuros,
  placeBelow,
  productDraft,
  saveProduct,
  type ProductDraft,
} from "./menu-edit";
import type { MenuEditor } from "./useMenuEditor";

interface Props {
  editor: MenuEditor;
  /** null for a new product in `categoryId`. */
  product: Product | null;
  categoryId: string;
  /** Opened from the Allergenen tab: scroll to the allergens. */
  showAllergens: boolean;
  onSaved: (id: string, categoryId: string) => void;
  onClose: () => void;
}

const dayMonth = (iso: string) => {
  const date = new Date(iso);
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}`;
};

/** Enter in a field never saves by accident (prototype). */
const noEnter = (event: KeyboardEvent) => {
  if (event.key === "Enter") event.preventDefault();
};

/**
 * The product editor of the prototype, in the right column: name, code, category, receipt name,
 * description, one price or sizes, ingredients (they make the "Zonder" button), allergens, choice
 * groups with free picks per product, alcohol, specialty and sold out.
 */
export function ProductEditor({ editor, product, categoryId, showAllergens, onSaved, onClose }: Props) {
  // Tafo-beheer can change the allergen list: the proposals follow at once
  useAllergenDictionary();
  const { menu, soldOut, toast } = editor;
  const [draft, setDraft] = useState<ProductDraft>(() =>
    productDraft(product, categoryId, product ? soldOut.has(product.id) : false),
  );
  const [ingredient, setIngredient] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const allergensRef = useRef<HTMLDivElement>(null);
  const set = (change: Partial<ProductDraft>) => {
    setDraft((current) => ({ ...current, ...change }));
  };
  const text = t.menuAdmin.product;
  const category = menu.categories.find((item) => item.id === draft.categoryId);

  useEffect(() => {
    if (showAllergens && allergensRef.current) bringIntoView(allergensRef.current);
  }, [showAllergens]);

  // a price changed in the list meanwhile (Furkan, 09/10/2026): the editor shows it, so saving here
  // afterwards does not put the old price back; a price typed here stays as it is typed
  const prices = product?.variants.map((variant) => variant.price) ?? [];
  const pricesKey = prices.join("|");
  const shownPrices = useRef(prices);
  useEffect(() => {
    const before = shownPrices.current;
    const now = pricesKey === "" ? [] : pricesKey.split("|").map(Number);
    shownPrices.current = now;
    setDraft((current) => followPrices(current, before, now));
  }, [pricesKey]);

  const receiptPreview = `${(draft.receiptName.trim() || draft.name.trim()).toUpperCase()}${
    draft.sizes && draft.variants[0] ? ` ${draft.variants[0].label.toUpperCase()}` : ""
  }`;

  const save = async () => {
    const problem = checkProduct(menu, draft, product);
    if (problem) {
      toast(problem, "bad");
      return;
    }
    const { menu: next, id } = saveProduct(menu, product, draft, withAllergens);
    const saved = next.products[id]!;
    if (!(await editor.commit(next, describe(product, saved)))) return;
    const wasSoldOut = product ? soldOut.has(product.id) : false;
    if (draft.soldOut !== wasSoldOut) await editor.setSoldOut(id, draft.soldOut);
    toast(product ? text.saved(saved.name) : text.added(saved.name));
    onSaved(id, saved.categoryId ?? categoryId);
  };

  const duplicate = async () => {
    const problem = checkProduct(menu, draft, product);
    if (problem) {
      toast(problem, "bad");
      return;
    }
    const copy = duplicateDraft(menu, draft);
    const { menu: next, id } = saveProduct(menu, null, { ...copy, soldOut: false }, withAllergens);
    // a copy keeps everything the original had that the editor does not show
    const original = product ? menu.products[product.id] : undefined;
    const copied: Menu = original
      ? { ...next, products: { ...next.products, [id]: { ...original, ...next.products[id]!, id } } }
      : next;
    const merged = product && copy.categoryId ? placeBelow(copied, copy.categoryId, id, product.id) : copied;
    if (await editor.commit(merged, `Product ${copy.name} toegevoegd (kopie)`)) {
      toast(text.copied);
      onSaved(id, copy.categoryId);
    }
  };

  const remove = async () => {
    if (!product) return;
    if (await editor.commit(deleteProduct(menu, product.id), `Product ${product.name} verwijderd`)) {
      toast(text.deleted(product.name));
      onClose();
    }
  };

  const addIngredient = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    const value = ingredient.trim();
    if (value && !draft.ingredients.includes(value)) set({ ingredients: [...draft.ingredients, value] });
    setIngredient("");
  };

  // allergens: the proposal follows what is typed; the toggles start from what the shop shows now
  const suggestion = suggestAllergens(menu, {
    name: draft.name,
    categoryId: draft.categoryId,
    ingredients: draft.ingredients,
    description: draft.description,
  });
  const view = product ? allergenView(menu, product) : null;
  const shown = draft.allergens ?? (view?.own ? [...view.certain, ...view.maybe] : suggestedCodes(suggestion));
  const sameAs = (a: readonly string[], b: readonly string[]) => a.slice().sort().join() === b.slice().sort().join();
  const ownList = view?.own ? [...view.certain, ...view.maybe] : null;
  const allergenStatus = (() => {
    const at = t.menuAdmin.allergens;
    if (view?.confirmed) {
      if (ownList && sameAs(shown, ownList)) {
        return (
          at.confirmedBy(view.confirmed.by, dayMonth(view.confirmed.at)) +
          (sameAs(ownList, suggestedCodes(suggestion)) ? "" : at.differs) +
          "."
        );
      }
      return at.changed;
    }
    return draft.allergens !== null || view?.own ? at.manual : at.proposal;
  })();
  const toggleAllergen = (code: string) => {
    set({ allergens: shown.includes(code) ? shown.filter((item) => item !== code) : [...shown, code] });
  };
  const choiceAllergens = draft.groups
    .map((groupId) => menu.modifierGroups[groupId])
    .filter((group) => group !== undefined && group.kind !== "INSTRUCTION")
    .map((group) => {
      const items = group!.items
        .map((item) => {
          const option = menu.products[item.productId];
          if (!option) return null;
          const found =
            option.categoryId !== null
              ? allergenView(menu, option)
              : suggestAllergens(menu, { ...option, ingredients: option.ingredients ?? [option.name] });
          const codes = ALLERGENS.filter(
            (allergen) => found.certain.has(allergen.code) || found.maybe.has(allergen.code),
          ).map(
            (allergen) => allergen.name + (found.certain.has(allergen.code) ? "" : ` (${t.menuAdmin.allergens.maybe})`),
          );
          return codes.length > 0 ? `${option.name} (${codes.join(", ")})` : null;
        })
        .filter((line) => line !== null);
      return items.length > 0 ? { title: group!.title, items } : null;
    })
    .filter((line) => line !== null);

  return (
    <div className="pe" aria-label={product ? product.name : text.new} role="region">
      <div className="pe-head">
        <h3>{product ? product.name : text.new}</h3>
      </div>
      <div className="pe-body">
        <div className="pegrid">
          <label className="field">
            {text.name}
            <input
              value={draft.name}
              placeholder={text.namePlaceholder}
              onKeyDown={noEnter}
              onChange={(event) => {
                set({ name: event.target.value });
              }}
              autoFocus={product === null}
            />
          </label>
          <label className="field">
            <span>
              {text.code} <span className="note">{text.codeHint}</span>
            </span>
            <input
              className="code"
              value={draft.code}
              placeholder={text.codePlaceholder}
              onKeyDown={noEnter}
              onChange={(event) => {
                set({ code: event.target.value.toUpperCase().replace(/\s/g, "") });
              }}
            />
          </label>
          <label className="field">
            {text.category}
            <select
              value={draft.categoryId}
              onChange={(event) => {
                set({ categoryId: event.target.value });
              }}
            >
              {menu.categories.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="field">
          <span>
            {text.receiptName} <span className="note">{text.receiptHint}</span>
          </span>
          <input
            value={draft.receiptName}
            placeholder={text.receiptPlaceholder}
            onKeyDown={noEnter}
            onChange={(event) => {
              set({ receiptName: event.target.value });
            }}
          />
        </label>
        <p className="note">
          {text.onReceipt} <b>{receiptPreview}</b>
        </p>
        <label className="field">
          <span>
            {text.description} <span className="note">{text.descriptionHint}</span>
          </span>
          <input
            value={draft.description}
            placeholder={text.descriptionPlaceholder}
            onKeyDown={noEnter}
            onChange={(event) => {
              set({ description: event.target.value });
            }}
          />
        </label>

        <div className="pesec">
          <div className="pesh">
            <b>{text.price}</b>
            <div className="seg" role="group" aria-label={text.priceMode}>
              <button
                type="button"
                aria-pressed={!draft.sizes}
                onClick={() => {
                  if (draft.sizes) set({ sizes: false, price: draft.variants[0]?.price ?? draft.price });
                }}
              >
                {text.onePrice}
              </button>
              <button
                type="button"
                aria-pressed={draft.sizes}
                onClick={() => {
                  if (draft.sizes) return;
                  // switching to sizes copies the single price into every size (prototype)
                  const fresh = !draft.variants.some((variant) => parseEuros(variant.price) > 0);
                  set({
                    sizes: true,
                    variants: fresh ? DEFAULT_SIZES.map((label) => ({ label, price: draft.price })) : draft.variants,
                  });
                }}
              >
                {text.sizes}
              </button>
            </div>
          </div>
          {draft.sizes ? (
            <div className="sizes">
              {draft.variants.map((variant, index) => (
                <div className="sizerow" key={index}>
                  <input
                    className="size-label"
                    value={variant.label}
                    aria-label={text.sizeLabel(index + 1)}
                    onKeyDown={noEnter}
                    onChange={(event) => {
                      set({
                        variants: draft.variants.map((item, at) =>
                          at === index ? { ...item, label: event.target.value } : item,
                        ),
                      });
                    }}
                  />
                  <span className="eur">€</span>
                  <input
                    className="num"
                    inputMode="decimal"
                    value={variant.price}
                    aria-label={text.priceOf(variant.label)}
                    onKeyDown={noEnter}
                    onChange={(event) => {
                      set({
                        variants: draft.variants.map((item, at) =>
                          at === index ? { ...item, price: event.target.value } : item,
                        ),
                      });
                    }}
                  />
                  {draft.variants.length > 1 && (
                    <button
                      type="button"
                      className="btn small"
                      aria-label={text.removeSize}
                      onClick={() => {
                        set({ variants: draft.variants.filter((_, at) => at !== index) });
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="btn small"
                onClick={() => {
                  set({ variants: [...draft.variants, { label: text.newSize, price: "0" }] });
                }}
              >
                {text.addSize}
              </button>
            </div>
          ) : (
            <div className="sizerow">
              <span className="eur">€</span>
              <input
                className="num"
                inputMode="decimal"
                value={draft.price}
                aria-label={text.price}
                onKeyDown={noEnter}
                onChange={(event) => {
                  set({ price: event.target.value });
                }}
              />
            </div>
          )}
          <p className="note">{text.priceNote}</p>
        </div>

        <div className="pesec">
          <div className="pesh">
            <b>{text.ingredients}</b>
            <span className="note">{text.ingredientsHint}</span>
          </div>
          <div className="ingchips">
            {draft.ingredients.map((name, index) => (
              <span className="ingchip" key={name}>
                {name}
                <button
                  type="button"
                  aria-label={text.removeIngredient(name)}
                  onClick={() => {
                    set({ ingredients: draft.ingredients.filter((_, at) => at !== index) });
                  }}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              className="ing-new"
              value={ingredient}
              placeholder={text.addIngredient}
              aria-label={text.addIngredient}
              onKeyDown={addIngredient}
              onChange={(event) => {
                setIngredient(event.target.value);
              }}
            />
          </div>
        </div>

        <div className="pesec" ref={allergensRef} id="pe-allergens">
          <div className="pesh">
            <b>{t.menuAdmin.allergens.title}</b>
            <button
              type="button"
              className="btn small"
              onClick={() => {
                set({ allergens: suggestedCodes(suggestion) });
                toast(t.menuAdmin.allergens.taken);
              }}
            >
              {t.menuAdmin.allergens.take}
            </button>
          </div>
          <div className="algtogs">
            {ALLERGENS.map((allergen) => {
              const kind = suggestion.certain.has(allergen.code) ? "z" : suggestion.maybe.has(allergen.code) ? "m" : "";
              return (
                <button
                  key={allergen.code}
                  type="button"
                  className={`algtog${kind ? ` sug ${kind}` : ""}`}
                  aria-pressed={shown.includes(allergen.code)}
                  title={kind ? t.menuAdmin.allergens.proposalTitle(whyText(suggestion, allergen.code)) : allergen.long}
                  onClick={() => {
                    toggleAllergen(allergen.code);
                  }}
                >
                  {allergen.name}
                </button>
              );
            })}
          </div>
          <p className="note">
            {allergenStatus} {t.menuAdmin.allergens.legendHint}
          </p>
          <div className="alggrp">
            <span className="note">{t.menuAdmin.allergens.choices}</span>
            {choiceAllergens.length > 0 ? (
              choiceAllergens.map((line) => (
                <div key={line.title}>
                  <b>{line.title}:</b> {line.items.join(" · ")}
                </div>
              ))
            ) : (
              <span className="note">{t.menuAdmin.allergens.noChoices}</span>
            )}
          </div>
        </div>

        <div className="pesec">
          <div className="pesh">
            <b>{text.groups}</b>
            <span className="note">{text.groupsHint}</span>
          </div>
          <div className="glinks">
            {Object.values(menu.modifierGroups).map((group) => {
              const on = draft.groups.includes(group.id);
              return (
                <div key={group.id} className={`glink${on ? " on" : ""}`}>
                  <button
                    type="button"
                    className="tog small"
                    aria-pressed={on}
                    aria-label={text.link(group.title)}
                    onClick={() => {
                      set({
                        groups: on ? draft.groups.filter((id) => id !== group.id) : [...draft.groups, group.id],
                      });
                    }}
                  >
                    {on ? "✓" : "–"}
                  </button>
                  <span>
                    <b>{group.title}</b>{" "}
                    <span className="note">
                      {groupLabel(group)} · {text.options(group.items.length)}
                    </span>
                  </span>
                  {on && (group.freeQuantity > 0 || draft.free[group.id] !== undefined) && (
                    <label className="note gfree">
                      {text.free}
                      <input
                        type="number"
                        min={0}
                        value={draft.free[group.id] ?? String(group.freeQuantity)}
                        aria-label={text.freeOf(group.title)}
                        onKeyDown={noEnter}
                        onChange={(event) => {
                          set({ free: { ...draft.free, [group.id]: event.target.value } });
                        }}
                      />
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="pesec">
          <div className="pesh">
            <b>
              {text.alcohol} <span className="note">{text.alcoholHint}</span>
            </b>
            <YesNo
              value={draft.alcohol}
              label={text.alcohol}
              onChange={(alcohol) => {
                set({ alcohol });
              }}
            />
          </div>
          <p className="note">{text.categoryVat(category?.vat?.takeaway ?? 6, category?.vat?.onSite ?? 12)}</p>
        </div>
        <div className="pesec">
          <div className="pesh">
            <b>
              {text.special} <span className="note">{text.specialHint}</span>
            </b>
            <YesNo
              value={draft.special}
              label={text.special}
              onChange={(special) => {
                set({ special });
              }}
            />
          </div>
        </div>
        <div className="pesec">
          <div className="pesh">
            <b>{text.soldOut}</b>
            <YesNo
              value={draft.soldOut}
              label={text.soldOut}
              onChange={(value) => {
                set({ soldOut: value });
              }}
            />
          </div>
        </div>
      </div>
      <div className="mfoot">
        {product && (
          <>
            <button
              type="button"
              className="btn danger"
              disabled={editor.busy}
              onClick={() => {
                setConfirmDelete(true);
              }}
            >
              {text.delete}
            </button>
            <button type="button" className="btn" disabled={editor.busy} onClick={() => void duplicate()}>
              {text.duplicate}
            </button>
          </>
        )}
        <span className="spacer" />
        <button type="button" className="btn" onClick={onClose}>
          {text.close}
        </button>
        <button type="button" className="btn primary" disabled={editor.busy} onClick={() => void save()}>
          {editor.busy ? t.menuAdmin.saving : text.save}
        </button>
      </div>
      {confirmDelete && product && (
        <ConfirmDialog
          message={text.confirmDelete(product.name)}
          confirm={text.delete}
          onConfirm={() => void remove()}
          onClose={() => {
            setConfirmDelete(false);
          }}
        />
      )}
    </div>
  );
}

/** A short line for the support log: what changed, with old and new prices. */
function describe(before: Product | null, after: Product): string {
  if (!before) return `Product ${after.name} toegevoegd`;
  const prices = after.variants
    .map((variant) => {
      const old = before.variants.find((item) => item.label === variant.label);
      if (!old || old.price === variant.price) return null;
      const label = after.variants.length > 1 ? ` ${variant.label}` : "";
      return `Prijs ${after.name}${label}: ${formatEuro(eurosToCents(old.price))} → ${formatEuro(eurosToCents(variant.price))}`;
    })
    .filter((line) => line !== null);
  return prices.length > 0 ? prices.join("; ") : `Product ${after.name} opgeslagen`;
}
